import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Checkbox, Typography, useTheme } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';

import { AppPopover } from '@shared/components/AppPopover';
import { TAnchor } from '@shared/hooks/useAnchor';
import { formatDate, withQueryParams } from '@shared/commonFunctions';
import { MarkAllRead, NotificationRow, NotificationDetails,TextWrapper, NotificationRowGroup } from '@styles/Layout/PopOversStyles';
import { dateFormatWithTime } from '@shared/constants/formats';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { useTabs } from '@shared/hooks/useTabs';
import { INotificationsResponse } from '../SMSNotifications';

interface INotificationsPreview {
  anchor: TAnchor;
  toggleAnchor(event?: React.MouseEvent<TAnchor> | null): void;
  title: string;
  items: INotificationsResponse[];
  fetchData?: () => void;
  markNotificationsAsRead?: (selectedNotifications: string[]) => void;
  hasMore?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const NotificationsPreview = ({ anchor, toggleAnchor, title, items, fetchData = () => {}, markNotificationsAsRead = () => {}, hasMore = false }: INotificationsPreview) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);

  const markAsReadText = useMemo(
    () => (selectedNotifications.length ? 'Mark Selected as Read' : 'Mark All as Read'),
    [selectedNotifications],
  );

  const onCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>, messageIds: string[]) => {
    if (event.target.checked) {
      setSelectedNotifications((prev) => [...prev, ...messageIds]);
    } else {
      setSelectedNotifications((prev) => prev.filter((notificationId: string) => !messageIds.includes(notificationId) ));
    }
  }, []);

  const navigateToContact = useCallback((contactId: string, title: string) => {
    const route = withQueryParams('/contact-manager', { id: contactId }, false);
    openTab({ title, route });
    navigate(route);
    toggleAnchor();
  }, []);

  const readNotifications = () => {
    if (markAsReadText === 'Mark All as Read') {
      const selectedMessageIds: string[] =[];
      items.map(item => selectedMessageIds.push(...item.messageIds));
      markNotificationsAsRead(selectedMessageIds);
    } else {
      markNotificationsAsRead(selectedNotifications);
    }
  };

  return (
    <>
      <AppPopover title={title} anchor={anchor} toggleAnchor={() => toggleAnchor()} width='445px'>
        {(items.length >= 1) && 
          <MarkAllRead>
            <Typography color={theme.palette.info.dark} variant="subtitle1" onClick={readNotifications}>
              {markAsReadText}
            </Typography>
          </MarkAllRead>
        }
        {items.length ? <InfiniteScroll
          dataLength={items.length}
          next={fetchData}
          hasMore={hasMore}
          loader={<LoaderBackdrop open={true}/>}
          height={250}
        >
        {items.map(({ contactId, senderName, messageIds, createDate, message }: INotificationsResponse, index: number) => (
          <NotificationRow key={index}>
            <Checkbox checked={selectedNotifications.some( notificationId => messageIds.includes(notificationId))} onChange={(e) => onCheckboxChange(e, messageIds)} />
            <NotificationDetails>
              <TextWrapper>
                <Typography variant="subtitle1" color={theme.palette.primary.main} onClick={() => { navigateToContact(contactId, senderName); }}>
                  {senderName} {messageIds.length>1 && ` (${messageIds.length})`}
                </Typography>
              </TextWrapper>
              <Typography variant="caption">{message}</Typography>
            </NotificationDetails>

            <NotificationRowGroup>
              <Typography variant="caption">{formatDate(createDate, { format: dateFormatWithTime })}</Typography>
              <TextWrapper>
                <Typography variant="subtitle1" color={theme.palette.primary.main} onClick={() => markNotificationsAsRead(messageIds)}>Mark as read</Typography>
              </TextWrapper>
            </NotificationRowGroup>
          </NotificationRow>
        ))}
        </InfiniteScroll> : <Typography sx={{ pl: 2 }}>No notifications</Typography>}    
      </AppPopover>
    </>
  );
};

